import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { Alert } from "reactstrap";
import { z, ZodError } from "zod";
import { REPORT_TEMPLATES } from "../mc-shared/reportAppConstants";
import { ZReportTemplateId } from "../mc-shared/reportSchemas/reportSchemas";
import { useGetProjectProjectIdQuery, usePostProjectByProjectIdReportboardsMutation } from "../../openapi";
import { FailedAlert, MCLoader } from "../components";

const ZReportDoksamParams = z
  .object({
    projectNo: z
      .string()
      .regex(/^\d{6,8}(-\d{1,10})?$/, "projectNo must be 6-8 digits optionally followed by a hyphen and 1-10 digits"),
    templateId: z.preprocess((value) => Number(value), ZReportTemplateId),
  });

type IReportDoksamParams = z.infer<typeof ZReportDoksamParams>;

const getProjectMainNo = (projectNo?: string): number => Number((projectNo ?? "").split("-")[0] ?? "");

const getProjectSubTask = (projectNo?: string): string | undefined => {
  const sub = (projectNo ?? "").split("-")[1];
  return sub ? sub : undefined;
};

const DoksamReportPath: React.FC<{ searchStr: string }> = ({ searchStr }) => {
  const history = useHistory();
  const [parsedParams, setParsedParams] = useState<IReportDoksamParams | null>(null);
  const [paramsError, setParamsError] = useState<ZodError | null>(null);
  const hasSubmittedRef = useRef(false);

  const createArgs = useMemo(() => {
    if (!parsedParams) {
      return null;
    }

    const templateName = REPORT_TEMPLATES.find((template) => template.templateId === parsedParams.templateId)?.name ?? "Ny rapport";

    return {
      projectNo: getProjectMainNo(parsedParams.projectNo),
      name: templateName,
      templateId: parsedParams.templateId,
      doksam: {
        subTaskNo: getProjectSubTask(parsedParams.projectNo),
      },
    };
  }, [parsedParams]);

  const {
    data: projectId,
    isLoading: isProjectIdLoading,
    isError: isProjectIdError,
    error: projectIdError,
  } = useGetProjectProjectIdQuery(
    { projectNo: createArgs?.projectNo ?? 0 },
    {
      skip: createArgs == null,
    },
  );

  const [createReport, { data: createdReport, isLoading: isCreateLoading, isError: isCreateError, error: createError }] =
    usePostProjectByProjectIdReportboardsMutation();

  useEffect(() => {
    localStorage.removeItem("gotolink");
    const parsed = queryString.parse(searchStr);

    setParamsError(null);
    hasSubmittedRef.current = false;
    try {
      const params = ZReportDoksamParams.parse(parsed);
      setParsedParams(params);
    } catch (e) {
      setParsedParams(null);
      setParamsError(e as ZodError);
    }
  }, [searchStr]);

  useEffect(() => {
    if (!createArgs || !projectId) {
      return;
    }

    if (createdReport != null || isCreateLoading || hasSubmittedRef.current) {
      return;
    }

    hasSubmittedRef.current = true;
    createReport({
      projectId,
      body: {
        name: createArgs.name,
        template: createArgs.templateId,
        doksam: createArgs.doksam,
      },
    });
  }, [createArgs, projectId, createReport, createdReport, isCreateLoading]);

  useEffect(() => {
    if (projectId && createdReport?._id) {
      history.push(`/project/${projectId}/reports/${createdReport._id}?doksam=true`);
    }
  }, [createdReport, history, projectId]);

  const errorMessage = (projectIdError as any)?.data ?? (createError as any)?.data ?? projectIdError ?? createError ?? null;
  const isError = isProjectIdError || isCreateError;
  const isLoading = isProjectIdLoading || isCreateLoading;

  return (
    <div>
      {isError && <FailedAlert errorMessage={errorMessage as any} />}
      {isLoading && <MCLoader />}
      {paramsError != null && (
        <div>
          <h4>Feil nettadresseparametere</h4>
          {paramsError.errors.map((err, i) => (
            <Alert color="danger" key={i}>
              <div>{JSON.stringify(err)}</div>
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default DoksamReportPath;
